import React from 'react';

import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { ServicePlanItem } from 'src/features/servicePlanItems/utils/types';
import ServicePlanItemFormModal from 'src/features/servicePlanItems/components/forms/ServicePlanItemFormModal';
import { useUpdateServicePlanItem } from 'src/features/servicePlanItems/api/useUpdateServicePlanItem';
import { LIST_SERVICE_PLAN_ITEMS_RESOURCE } from 'src/features/servicePlanItems/api/useListServicePlanItems';
import { servicePlanItemsMessages } from 'src/features/servicePlanItems/utils/messages';
import { SERVICE_PLAN_ITEM_RESOURCE } from 'src/features/servicePlanItems/api/useGetServicePlanItem';

interface UpdateServicePlanItemFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedServicePlanItem: ServicePlanItem;
    children?: React.ReactNode;
}

function UpdateServicePlanItemFormModal<TData>({
                                                   className,
                                                   semantics,
                                                   children,
                                                   selectedServicePlanItem,
                                               }: UpdateServicePlanItemFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { mutate, isPending } = useUpdateServicePlanItem({
        onSuccess: ({ name }) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SERVICE_PLAN_ITEMS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [SERVICE_PLAN_ITEM_RESOURCE] });
            toast({
                title: formatMessage(servicePlanItemsMessages.updateServicePlanItemSuccess),
                description: formatMessage(servicePlanItemsMessages.updateServicePlanItemSuccessDescription, {
                    entity: name,
                }),
            });
        },
    });

    return (
        <ServicePlanItemFormModal
            selectedServicePlanItem={selectedServicePlanItem}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ ...values, id: selectedServicePlanItem.id }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(servicePlanItemsMessages.editServicePlanItemSubtitle, {
                entity: selectedServicePlanItem.name,
            })}
        >
            {children}
        </ServicePlanItemFormModal>
    );
}

export default UpdateServicePlanItemFormModal;
